import * as React from "react";

import { Center, Tag as ChakraTag } from "@chakra-ui/react";
import { FeedbackIcons } from "../../theme/icons";
import { useColorTokens } from "../../theme/hooks";

export interface TagProps extends Omit<ChakraTag.RootProps, "variant"> {
  startElement?: React.ReactNode;
  endElement?: React.ReactNode;
  onClose?: VoidFunction;
  closable?: boolean;
  status?: "success" | "error" | "warning" | "info";
  variant?: "solid" | "outline" | "surface";
  dataTracking?: string;
  onClick?: VoidFunction;
  labelProps?: React.ComponentProps<typeof ChakraTag.Label>;
  textStyle?: string;
  isShowIcon?: boolean;
}

const Tag = React.forwardRef<HTMLSpanElement, TagProps>((props, ref) => {
  const {
    startElement,
    endElement,
    onClose,
    onClick,
    closable = !!onClose,
    status = "info",
    variant = "solid",
    labelProps = {},
    dataTracking,
    children,
    textStyle,
    isShowIcon = true,
    ...rest
  } = props;

  const { success, danger, warning, base } = useColorTokens();

  const getIcon = (type: string) => {
    switch (type) {
      case "success":
        return <FeedbackIcons.Tick size="xs" color="success.800" />;
      case "error":
        return <FeedbackIcons.Error size="xs" color="danger.500" />;
      case "warning":
        return <FeedbackIcons.Warning size="xs" color="warning.600" />;
      case "info":
        return <FeedbackIcons.Info size="xs" color="action.600" />;
      default:
        return <FeedbackIcons.Info size="xs" color="action.500" />;
    }
  };

  // Color logic based on status and variant
  const getColors = () => {
    switch (status) {
      case "success":
        return {
          bg: variant !== "solid" ? success[300] : success[400],
          border: variant === "outline" ? success[700] : "none",
          text: "base.700"
        };
      case "error":
        return {
          bg: danger[300],
          border: variant === "outline" ? danger[600] : "none",
          text: "base.700"
        };
      case "warning":
        return {
          bg: variant !== "solid" ? warning[300] : warning[400],
          border: variant === "outline" ? warning[600] : "none",
          text: "base.700"
        };
      case "info":
      default:
        return {
          bg: variant !== "solid" ? base[300] : base[400],
          border: variant === "outline" ? base[600] : "none",
          text: "base.700"
        };
    }
  };

  const { bg, border, text } = getColors();

  // Default styles that can be overridden by passing in Chakra props
  const defaultStyles = {
    borderStyle: variant === "outline" ? "solid" : "none",
    borderRadius: "4px",
    paddingY: "6px",
    paddingX: "12px",
    boxShadow: "none",
    borderWidth: variant === "outline" ? "1px" : "0",
    borderColor: variant === "outline" ? border : "transparent",
    backgroundColor: bg,
    color: text,
    gap: "4px"
  };

  return (
    <ChakraTag.Root
      data-bark-component="Tag"
      ref={ref}
      onClick={onClick}
      data-tracking-id={dataTracking}
      variant={variant}
      {...defaultStyles}
      {...rest}
      css={{
        "& > *:first-of-type": {
          marginLeft: "0 !important"
        },
        "& > *:last-of-type": {
          marginRight: "0 !important"
        }
      }}
      data-testid="tag-root"
    >
      {variant !== "surface" && isShowIcon && (
        <ChakraTag.StartElement
          marginRight="4px"
          data-testid="tag-start-element"
        >
          <Center>{startElement || getIcon(status)}</Center>
        </ChakraTag.StartElement>
      )}

      <ChakraTag.Label
        textStyle={textStyle ?? "body.small"}
        color="base.700"
        lineHeight="16px"
        fontWeight="medium"
        flex="1"
        {...labelProps}
        data-testid="tag-label"
      >
        {children}
      </ChakraTag.Label>

      {endElement && (
        <ChakraTag.EndElement marginLeft="4px" data-testid="tag-end-element">
          {endElement}
        </ChakraTag.EndElement>
      )}

      {closable && (
        <ChakraTag.EndElement marginLeft="4px">
          <ChakraTag.CloseTrigger onClick={onClose} data-testid="tag-close" />
        </ChakraTag.EndElement>
      )}
    </ChakraTag.Root>
  );
});

export default Tag;
